// Date parsing utilities. All dates round-trip through ISO YYYY-MM-DD strings.

import { NUMBER_WORDS, parseWholeNumber, parseYear } from './units.js';
import { fuzzyLookup } from './phonetic.js';

const MONTHS: Record<string, number> = {
  january: 1, jan: 1,
  february: 2, feb: 2,
  march: 3, mar: 3,
  april: 4, apr: 4,
  may: 5,
  june: 6, jun: 6,
  july: 7, jul: 7,
  august: 8, aug: 8,
  september: 9, sept: 9, sep: 9,
  october: 10, oct: 10,
  november: 11, nov: 11,
  december: 12, dec: 12,
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function isoToDDMMYYYY(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function ddmmyyyyToIso(s: string): string | null {
  const m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/.exec(s.trim());
  if (!m) return null;
  let day = Number(m[1]);
  let month = Number(m[2]);
  let year = Number(m[3]);
  if (m[3]!.length === 2) year = 2000 + year;
  return validateDate(year, month, day);
}

function validateDate(year: number, month: number, day: number): string | null {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  // JS Date construction with overflow rolls over; check round-trip.
  const d = new Date(Date.UTC(year, month - 1, day));
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() + 1 !== month ||
    d.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Parse a date in any of:
 *   - "12 March 2024"
 *   - "12 Mar 2024"
 *   - "March 12 2024" / "March 12, 2024"
 *   - "12/03/2024" / "12-03-2024" / "12.03.2024"  (DD/MM/YYYY)
 *   - "twelve March twenty twenty four"
 *   - "the twelfth of March two thousand and twenty four"
 *
 * Returns ISO YYYY-MM-DD, or null if not parseable.
 */
export function parseDate(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;

  // 1) Pure numeric DD/MM/YYYY (or with - or .)
  const iso = ddmmyyyyToIso(raw);
  if (iso) return iso;

  // 2) Already ISO?
  const isoDirect = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (isoDirect) {
    return validateDate(Number(isoDirect[1]), Number(isoDirect[2]), Number(isoDirect[3]));
  }

  // Normalise: snap misspelled date words onto the known vocabulary,
  // then remove "the", "of", commas, ordinal suffixes
  const cleaned = fuzzDateWords(raw.toLowerCase())
    .replace(/\bthe\b/g, ' ')
    .replace(/\bof\b/g, ' ')
    .replace(/,/g, ' ')
    .replace(/(\d+)(st|nd|rd|th)\b/g, '$1')
    .replace(/(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth|twenty[\s-]first|twenty[\s-]second|twenty[\s-]third|twenty[\s-]fourth|twenty[\s-]fifth|twenty[\s-]sixth|twenty[\s-]seventh|twenty[\s-]eighth|twenty[\s-]ninth|thirtieth|thirty[\s-]first)/g, (m) => ordinalToCardinal(m))
    .replace(/\s+/g, ' ')
    .trim();

  // 3) "<day> <month> <year>" or "<day-word> <month> <year-words>"
  // Try day first.
  const dayParse = parseWholeNumber(cleaned);
  if (dayParse) {
    const after = cleaned.slice(dayParse.len).trim();
    const monthMatch = /^([a-z]+)\s+(.+)$/i.exec(after);
    if (monthMatch) {
      const monthName = monthMatch[1]!.toLowerCase();
      const monthNum = MONTHS[monthName];
      if (monthNum) {
        const year = parseYear(monthMatch[2]!);
        if (year) {
          return validateDate(year.value, monthNum, dayParse.value);
        }
      }
    }
  }

  // 4) "<month> <day> <year>"
  const monthFirst = /^([a-z]+)\s+(\S+)\s+(.+)$/i.exec(cleaned);
  if (monthFirst) {
    const monthName = monthFirst[1]!.toLowerCase();
    const monthNum = MONTHS[monthName];
    if (monthNum) {
      const dayPart = parseWholeNumber(monthFirst[2]!);
      if (dayPart && dayPart.len === monthFirst[2]!.length) {
        const year = parseYear(monthFirst[3]!);
        if (year) {
          return validateDate(year.value, monthNum, dayPart.value);
        }
      }
    }
  }

  return null;
}

const ORDINAL_MAP: Record<string, string> = {
  first: '1', second: '2', third: '3', fourth: '4', fifth: '5',
  sixth: '6', seventh: '7', eighth: '8', ninth: '9', tenth: '10',
  eleventh: '11', twelfth: '12', thirteenth: '13', fourteenth: '14',
  fifteenth: '15', sixteenth: '16', seventeenth: '17', eighteenth: '18',
  nineteenth: '19', twentieth: '20', thirtieth: '30',
};

// Every word that can legitimately appear inside a spoken date. Words
// outside this set are never touched; words close to it (edit distance
// 1 for 5–7 letters, 2 for 8+) are snapped onto it, so STT spelling
// drift — "Twelveth", "Febuary", "januray" — stops killing date parses.
const DATE_WORD_VOCAB: readonly string[] = [
  ...Object.keys(MONTHS),
  ...Object.keys(ORDINAL_MAP),
  ...Object.keys(NUMBER_WORDS),
  'thousand',
];

function fuzzDateWords(lowered: string): string {
  return lowered
    .split(/\s+/)
    .map((w) => {
      const stripped = w.replace(/[^a-z-]/g, '');
      if (!stripped || /\d/.test(w)) return w;
      const mapped = stripped
        .split('-')
        .map((part) => fuzzyLookup(part, DATE_WORD_VOCAB) ?? part)
        .join('-');
      return mapped === stripped ? w : w.replace(stripped, mapped);
    })
    .join(' ');
}

function ordinalToCardinal(word: string): string {
  const map = ORDINAL_MAP;
  const cleaned = word.replace(/[\s-]/g, '');
  if (map[cleaned]) return map[cleaned]!;
  // twenty-first .. twenty-ninth, thirty-first
  const compound = /^(twenty|thirty)(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth)$/.exec(cleaned);
  if (compound) {
    const tens = compound[1] === 'twenty' ? 20 : 30;
    const ones = map[compound[2]!];
    if (ones) return String(tens + Number(ones));
  }
  return word;
}

/** Render an ISO date in the chosen export format. */
export function formatDate(iso: string | null, format: 'DD/MM/YYYY' | 'YYYY-MM-DD'): string {
  if (!iso) return '';
  if (format === 'YYYY-MM-DD') return iso;
  return isoToDDMMYYYY(iso);
}
